import {
  type InvalidateQueryFilters,
  QueryClient,
  type QueryFunction,
  type QueryKey,
} from '@tanstack/react-query';
import { create } from 'zustand';

interface QueryClientStore {
  queryClient: QueryClient; // Store the QueryClient instance
  setQueryClient: (client: QueryClient) => void;
  invalidateQueries: (queryKey: string[]) => void;
  prefetchQuery: <T>(
    queryKey: QueryKey,
    queryFn: QueryFunction<T>,
    options?: { staleTime?: number },
  ) => Promise<void>;
}

const queryClientDefaults = {
  queries: {
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  },
};

const useQueryClientStore = create<QueryClientStore>((set, get: any) => ({
  queryClient: new QueryClient({
    defaultOptions: queryClientDefaults,
  }),
  setQueryClient: (client: QueryClient) => {
    set({ queryClient: client }); // Update the QueryClient in the store
  },
  invalidateQueries: async (
    queryKey: InvalidateQueryFilters<readonly unknown[]>,
  ) => {
    const queryClient = get().queryClient; // Get the QueryClient from the store
    await queryClient.invalidateQueries(queryKey);
  },
  prefetchQuery: async (queryKey, queryFn, options) => {
    const queryClient = get().queryClient; // Get the QueryClient from the store
    await queryClient.prefetchQuery({
      queryKey,
      queryFn,
      staleTime: options?.staleTime,
    });
  },
}));

export { useQueryClientStore, queryClientDefaults };
